import React from 'react';
import { CloudDownload, Upload, Download, Film, PersonStanding, Lock } from 'lucide-react';
import { useDanceStore } from '../../store/useDanceStore';
import { useAuthStore } from '../../store/useAuthStore';
import { ExplorerView } from './ExplorerView';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../lib/analytics';

export const ImportExportModule: React.FC = () => {
  const { t } = useTranslation();

  const importFile = useDanceStore(state => state.importFile);
  const exportSequence = useDanceStore(state => state.exportSequence);
  const exportPose = useDanceStore(state => state.exportPose);
  const user = useAuthStore(state => state.user);

  const handleImport = (type: 'pose' | 'sequence') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'pose' ? '.json,.cpos' : '.json,.cseq';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) importFile(file);
    };
    input.click();
  };

  const actionButtonBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    border: '1px solid',
    transition: 'all 0.2s',
    width: '100%',
    textAlign: 'left',
  };

  const content = (
    <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!user && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: '8px',
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          marginBottom: 4,
        }}>
          <Lock size={14} color="#fbbf24" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fbbf24' }}>
              {t('nav.unavailable', 'Feature Locked')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              {t('nav.registered_only', 'For Registered users only')}
            </div>
          </div>
        </div>
      )}

      {/* Import Section */}
      <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Upload size={12} />
          {t('nav.import', 'Import')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            disabled={!user}
            onClick={() => { trackEvent('import_pose_clicked'); handleImport('pose'); }}
            style={{
              ...actionButtonBase,
              background: user ? 'rgba(56, 189, 248, 0.08)' : 'rgba(255,255,255,0.03)',
              borderColor: user ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.08)',
              color: user ? '#38bdf8' : 'rgba(255,255,255,0.25)',
              opacity: user ? 1 : 0.5,
              cursor: user ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.18)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.45)'; } }}
            onMouseLeave={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.25)'; } }}
          >
            <PersonStanding size={16} style={{ flexShrink: 0 }} />
            <div>
              <div>{t('nav.import_pose', 'Import Pose')}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 400, color: user ? 'rgba(56,189,248,0.6)' : 'rgba(255,255,255,0.2)', marginTop: 1 }}>{t('import_export.pose_formats')}</div>
            </div>
          </button>

          <button
            disabled={!user}
            onClick={() => { trackEvent('import_sequence_clicked'); handleImport('sequence'); }}
            style={{
              ...actionButtonBase,
              background: user ? 'rgba(56, 189, 248, 0.08)' : 'rgba(255,255,255,0.03)',
              borderColor: user ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.08)',
              color: user ? '#38bdf8' : 'rgba(255,255,255,0.25)',
              opacity: user ? 1 : 0.5,
              cursor: user ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.18)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.45)'; } }}
            onMouseLeave={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.25)'; } }}
          >
            <Film size={16} style={{ flexShrink: 0 }} />
            <div>
              <div>{t('nav.import_sequence', 'Import Sequence')}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 400, color: user ? 'rgba(56,189,248,0.6)' : 'rgba(255,255,255,0.2)', marginTop: 1 }}>{t('import_export.sequence_formats')}</div>
            </div>
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={12} />
          {t('nav.export', 'Export')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            disabled={!user}
            onClick={() => { trackEvent('export_pose_clicked'); exportPose(); }}
            style={{
              ...actionButtonBase,
              background: user ? 'rgba(56, 189, 248, 0.08)' : 'rgba(255,255,255,0.03)',
              borderColor: user ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.08)',
              color: user ? '#38bdf8' : 'rgba(255,255,255,0.25)',
              opacity: user ? 1 : 0.5,
              cursor: user ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.18)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.45)'; } }}
            onMouseLeave={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.25)'; } }}
          >
            <PersonStanding size={16} style={{ flexShrink: 0 }} />
            <div>
              <div>{t('nav.export_pose', 'Export Pose')}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 400, color: user ? 'rgba(56,189,248,0.6)' : 'rgba(255,255,255,0.2)', marginTop: 1 }}>{t('import_export.exports_as_cpos')}</div>
            </div>
          </button>

          <button
            disabled={!user}
            onClick={() => { trackEvent('export_sequence_clicked'); exportSequence(); }}
            style={{
              ...actionButtonBase,
              background: user ? 'rgba(56, 189, 248, 0.08)' : 'rgba(255,255,255,0.03)',
              borderColor: user ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.08)',
              color: user ? '#38bdf8' : 'rgba(255,255,255,0.25)',
              opacity: user ? 1 : 0.5,
              cursor: user ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.18)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.45)'; } }}
            onMouseLeave={(e) => { if (user) { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.25)'; } }}
          >
            <Film size={16} style={{ flexShrink: 0 }} />
            <div>
              <div>{t('nav.export_sequence', 'Export Sequence')}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 400, color: user ? 'rgba(56,189,248,0.6)' : 'rgba(255,255,255,0.2)', marginTop: 1 }}>{t('import_export.exports_as_cseq')}</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <CloudDownload size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>
          {t('nav.import_export', 'Import / Export')}
        </h2>
        <p className="module-header-subtitle" style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
          {t('import_export.subtitle', 'Transfer poses and sequences')}
        </p>
      </div>
    </div>
  );

  return <ExplorerView title={titleNode} content={content} />;
};
